import {partition} from '@benchling/js-utils';
import assert from 'assert';
import React from 'react';

import {TerminusStyle} from 'antibodies/AntibodyConstants';
import {AntibodyFormat, DomainDisplayParams, SizingParams} from 'antibodies/AntibodyTypedefs';
import {domainColor} from 'antibodies/AntibodyUtils';
import AntibodyDomain from 'antibodies/visualization/AntibodyDomain';
import AntibodyHinge from 'antibodies/visualization/AntibodyHinge';
import AntibodyLinker from 'antibodies/visualization/AntibodyLinker';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain.
 * @param translate How far to move the domain, both horizontally and vertically
 * @param rotate How many degrees clockwise to rotate the domain
 * @param reflect Whether to reflect the domain horizontally
 */
function getTransform({translate, rotate, reflect}: DomainDisplayParams): string {
  const translateX = translate?.x ?? 0;
  const translateY = translate?.y ?? 0;
  const translation = `translate(${translateX}, ${translateY})`;
  // Rotate around the top left corner of the bounding box of the domain, not around the origin of the SVG.
  const rotation = rotate != null ? `rotate(${rotate} ${translateX} ${translateY})` : '';
  const reflection = reflect ? 'scale(-1, 1)' : '';
  return [rotation, translation, reflection].join(' ');
}

export interface AntibodyProps {
  displayByRef: Record<number, DomainDisplayParams>;
  sizing: SizingParams;
  format: AntibodyFormat;
}

/**
 * An SVG visualization of an antibody.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * Right now, all of these props are taken from one of six hard-coded objects in `AntibodyConstants`.
 * @param displayByRef A map from domain ref (a numerical identifier of the domain within the antibody) to a
 *                     display params object.
 * @param domainSizing Parameters for how big individual chain domains should be
 * @param format The format for the antibody.
 */
export default function Antibody({
  displayByRef,
  sizing: {chainDomainSizing, connectorSizing, svgSizing},
  format,
}: AntibodyProps): JSX.Element {
  assert(
    format.domains.every(({type}) => type !== AntibodyDomainType.J),
    "we don't know how to make joiners yet"
  );

  const [hinges, nonHinges] = partition(format.domains, ({type}) => type === AntibodyDomainType.H);
  const [linkers, chainDomains] = partition(nonHinges, ({type}) => type === AntibodyDomainType.L);
  const {svgViewboxHeight, svgViewboxWidth} = svgSizing;
  return (
    <>
      <svg height="100%" width="100%" viewBox={`0 0 ${svgViewboxWidth} ${svgViewboxHeight}`}>
        {hinges.map((hinge) => {
          const display = displayByRef[hinge.ref];
          assert(
            display.type === AntibodyDomainType.H,
            'Received non-hinge display params for a hinge domain'
          );
          const transform = getTransform(display);
          const {strokeThickness} = connectorSizing;
          return (
            <g key={hinge.ref} transform={transform}>
              <AntibodyHinge
                angle={display.angle}
                color={domainColor(hinge.type)}
                fixedEndLength={display.fixedEndLength}
                freeEndLength={display.freeEndLength}
                strokeThickness={strokeThickness}
              />
            </g>
          );
        })}
        {linkers.map((linker) => {
          const display = displayByRef[linker.ref];
          assert(
            display.type === AntibodyDomainType.L,
            'Received non-linker display params for a linker domain'
          );
          const transform = getTransform(display);
          const {strokeThickness} = connectorSizing;
          return (
            <g key={linker.ref} transform={transform}>
              <AntibodyLinker
                centerLength={display.centerLength}
                color={domainColor(linker.type)}
                endRadius={display.endRadius}
                strokeThickness={strokeThickness}
                tipLength={display.tipLength}
              />
            </g>
          );
        })}
        {chainDomains.map((domain) => {
          const display = displayByRef[domain.ref];
          assert(
            // Need to do all three checks rather than an .includes to satisfy typescript
            display.type !== AntibodyDomainType.J &&
              display.type !== AntibodyDomainType.H &&
              display.type !== AntibodyDomainType.L,
            'Received non-chain display params for a chain domain'
          );
          const transform = getTransform(display);
          const {cornerRadius, height, width} = chainDomainSizing;
          return (
            <g key={domain.ref} transform={transform}>
              <AntibodyDomain
                color={domainColor(domain.type, domain.specificity)}
                cornerRadius={cornerRadius}
                height={height}
                cTerminusStyle={display.cTerminusStyle ?? TerminusStyle.FLAT}
                nTerminusStyle={display.nTerminusStyle ?? TerminusStyle.FLAT}
                width={width}
              />
            </g>
          );
        })}
      </svg>
    </>
  );
}
